import React, { useRef, useState } from "react";
import dayjs, { Dayjs } from 'dayjs';

import { useAppStore } from "../../../shared/stores/useAppStore";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useScenarioTree } from "../../../shared/hooks/queries/useScenarioTree";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";
import FieldBindingModal from "../../FieldBindingModal";
import CustomDatePicker from "./reusables/CustomDatePicker";
import snakeToPascal from "../../../tools";
import { initTimeSerie, initTimeSerieTiming } from "./Initializer";
import apiService from "../../../services/ApiService";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import FieldError from "../FieldError";
import ErpBindingButton, { ErpBinding } from "./reusables/ErpBindingButton";


export default function TimeSerieTimingField({ name, field }) {
    const hasErpConnections = useAppStore(s => s.hasErpConnections);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const { state } = useScenarioTree(scenarioId);
    const queryClient = useQueryClient();

    const [mounted, setMounted] = React.useState(false);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const [bindingModalOpen, setBindingModalOpen] = React.useState(false);
    const [bindingKey, setBindingKey] = React.useState<string | null>(null);
    const [bindingRefreshTrigger, setBindingRefreshTrigger] = React.useState(0);
    const [existingBinding, setExistingBinding] = React.useState<ErpBinding | null>(null);

    const handleOpenBindingModal = (key: string, binding: ErpBinding | null) => {
        setBindingKey(key);
        setExistingBinding(binding);
        setBindingModalOpen(true);
    };

    const handleBindingCreated = () => {
        setBindingRefreshTrigger(prev => prev + 1);
    };

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    const checkComplete = () => {
        if (!field.currentValue?.value) {
            field.complete = false;
            field.error = null;
            return;
        }

        const value = field.currentValue.value;

        if (value.start_date.value && value.end_date.value) {
            if (value.start_date.value.diff(value.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.error = 'Start date must be before End date.'
            }
            else if (value.start_date.value.diff(value.end_date.value, 'month') == 0) {
                field.complete = false;
                field.error = 'Difference Between Dates should be at least one month.'
            }
            else if (value.periodicity.value !== 'null') {
                field.complete = true;
                field.error = null;
            }
            else {
                field.error = null;
            }
        }
        else {
            field.complete = false;
            field.error = null;
        }
    }

    // Should be in obj instanciation (strong typing)
    // TODO strong typing
    if (field.currentValue === undefined) {
        initTimeSerieTiming(field);
        checkComplete();
    }

    const getTimeSeries = (item, timing_id) => {

        const timeSeries = Object.values(item.fields).filter((e: any) => (
            e.type === 'time_serie' && e.currentValue?.value?.timing_id == timing_id
        ))
        return timeSeries
    }

    const getTimeSerieLinked = (field) => {
        const inputTree = state.Input.tree;

        const treeItems = Object.values(inputTree.items).filter((e: any) => e.fields && Object.keys(e.fields).length !== 0);

        const timeSeries = [];

        treeItems.map(e => { timeSeries.push(...getTimeSeries(e, field.id)) })

        return timeSeries
    }

    const invalidateQueries = () => {
        // Only invalidate section-data cache so stale sections are refetched on next
        // open.  Do NOT invalidate SCENARIO_TREE_KEY: its queryFn returns an empty
        // initial state, so an invalidation-triggered refetch would wipe the
        // lazily-loaded tree and cause model-data items to disappear.
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const refreshTimeSeriesLinked = (field) => {
        const timeSeries = getTimeSerieLinked(field);

        timeSeries.map(async(e) => {
            await apiService.getTimeSerie(e.id).then(async(response) => {
                if (response) {
                    const newTs = await response.json();

                    e.currentValue.value.data = newTs.data;
                    initTimeSerie(e)
                    reloadLocalState();
                }
            })
            invalidateQueries();
        })
    }

    const handleChangestart_date = (newValue) => {
        if (!field.currentValue?.value) return;
        field.compute_error = null;
        reloadLocalState();

        const timing = field.currentValue.value;
        // Compute iterates at day=1; persisting any other day causes TS/window mismatch.
        const normalized = newValue.date(1);

        triggerSave();
        apiService.putModelDataTimingValue(field.id,
            normalized.format('YYYY-MM-DD'),
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            'monthly',
        ).then(async(response) => {
            if (response) {
                timing.start_date.value = normalized;
                field.start_dateStore.isOpen = false;
                checkComplete();
                refreshTimeSeriesLinked(field);
                resolveSave();
                reloadLocalState();
            }
        })

    };

    const handleChangeend_date = (newValue) => {
        if (!field.currentValue?.value) return;
        field.compute_error = null;
        reloadLocalState();

        const timing = field.currentValue.value;
        // end_date is normalized to the last day of the month (end of period).
        const normalized = newValue.endOf('month').startOf('day');

        triggerSave();
        apiService.putModelDataTimingValue(field.id,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            normalized.format('YYYY-MM-DD'),
            'monthly',
        ).then(async(response) => {
            if (response) {
                timing.end_date.value = normalized;
                field.end_dateStore.isOpen = false;
                checkComplete();
                refreshTimeSeriesLinked(field);
                resolveSave();
                reloadLocalState();
            }
        })

    };

    checkComplete();

    return (
        <div className="ml-4">
            <div className="mb-3.5">
                <div className="mb-1 flex items-center gap-1">
                    Start Date
                    {hasErpConnections && (
                        <ErpBindingButton
                            onClick={(binding) => handleOpenBindingModal('start_date', binding)}
                            fieldId={field.id}
                            bindingKey="start_date"
                            refreshTrigger={bindingRefreshTrigger}
                            currentValue={field.currentValue?.value?.start_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.start_date?.value}
                            appFieldType="datetime"
                            onSyncApply={(erpValue) => handleChangestart_date(erpValue)}
                        />
                    )}
                </div>
                <CustomDatePicker
                    item={field.start_dateStore}
                    currentValue={(field.currentValue?.value?.start_date?.value) ? field.currentValue.value.start_date.value : null}
                    handleChange={handleChangestart_date}
                />
            </div>
            <div className="mb-3.5">
                <div className="mb-1 flex items-center gap-1">
                    End Date
                    {hasErpConnections && (
                        <ErpBindingButton
                            onClick={(binding) => handleOpenBindingModal('end_date', binding)}
                            fieldId={field.id}
                            bindingKey="end_date"
                            refreshTrigger={bindingRefreshTrigger}
                            currentValue={field.currentValue?.value?.end_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.end_date?.value}
                            appFieldType="datetime"
                            onSyncApply={(erpValue) => handleChangeend_date(erpValue)}
                        />
                    )}
                </div>
                <CustomDatePicker
                    item={field.end_dateStore}
                    currentValue={(field.currentValue?.value?.end_date?.value) ? field.currentValue.value.end_date.value : null}
                    handleChange={handleChangeend_date}
                />
            </div>
            <SaveIndicator status={saveStatus} className="mt-1" />
            <FieldError field={field} />
            {!field.compute_error && field.error && (
                <div className="flex items-center gap-1 mt-1.5 px-1.5 py-1 w-fit bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <span className="material-symbols-outlined text-[14px]">error</span>
                    {field.error}
                </div>
            )}
            {bindingKey && (
                <FieldBindingModal
                    open={bindingModalOpen}
                    setOpen={setBindingModalOpen}
                    fieldName={name}
                    field={field}
                    modelDataId={field.model_data_id}
                    modelDataName={name}
                    scenarioId={scenarioId}
                    bindingKey={bindingKey}
                    existingBinding={existingBinding}
                    onBindingCreated={handleBindingCreated}
                    currentValue={
                        bindingKey === 'start_date'
                            ? field.currentValue?.value?.start_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.start_date?.value
                            : field.currentValue?.value?.end_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.end_date?.value
                    }
                    onSyncApply={(erpValue) => {
                        if (bindingKey === 'start_date') {
                            handleChangestart_date(erpValue);
                        } else if (bindingKey === 'end_date') {
                            handleChangeend_date(erpValue);
                        }
                    }}
                />
            )}
        </div>
    )
}
